/* Verbatim port of WS.renderStrip (service/review/index.html ~4394-4439) —
 * the §0 "what changed" strip (#wsStrip). Takes the Workspace instance and
 * the current AppPayloadState explicitly (F1/F2 porting rule "a") instead
 * of closing over `this`/global STATE.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * Pure template-building, no wiring beyond plain `<a href="#sN">` anchors
 * (the browser's own hash-navigation, not a click handler) — converts
 * cleanly. `esc()` dropped.
 *
 * F4 item 3 (store decoupling): subscribes to wsChangeStore (state/
 * wsChange.ts) below so a WS data change re-renders the strip, replacing
 * the `WS.renderStrip()` instance-method call state/workspace.ts's
 * `repaintAll()`/`init()` used to make directly (that method is REMOVED —
 * dead wiring once this subscription is the only reason it existed). The
 * subscription callback references the bare global `WS` (state/
 * workspace.ts's `declare global` block) rather than importing it — the
 * ONE place in this file that does, since a module-scope subscription
 * callback has no caller to receive `ws` as an explicit parameter from;
 * `renderStrip`'s own exported signature keeps taking `ws` as a real
 * parameter (rule "a"), unchanged.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { pct } from '../lib/format.ts';
import { payloadStore } from '../state/payload.ts';
import { wsChangeStore } from '../state/wsChange.ts';
import { scopeProvenanceLine } from '../derive/scope.ts';
import type { Workspace } from '../state/workspace.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export function renderStrip(ws: Workspace, state: AppPayloadState): void {
  const el = document.getElementById('wsStrip'); if (!el) return;
  // Scope-provenance line (design F1): a profile-scoped run must announce what
  // it deliberately left out, so a skipped layer never reads as ordinary
  // absence. Rendered ABOVE the "what changed" chips and INDEPENDENTLY of
  // workspace availability — it describes the loaded run, not the board — so a
  // freshly-loaded run with no shared board still shows its scope.
  const scope = scopeProvenanceLine(state.profileScope, state.skippedLayers);
  const scopeTpl = scope
    ? html`<div class="wsscope" title="This run was tailored by the client's research profile — skipped layers were deliberately not checked, not missing.">${scope}</div>`
    : nothing;
  if (ws.available !== true) { render(scopeTpl, el); return; }
  const chips: TemplateResult[] = [];
  const T = ws.tasks;
  const reg = T.filter((t) => t.status === 'regressed').length;
  const pend = T.filter((t) => t.status === 'implemented').length;
  const stalled = T.filter((t) => /^waiting/.test(t.status) && ws.ageDays(t.updated_at) > 14).length;
  if (reg) chips.push(html`<a class="wchip warn" href="#s7"><b>${reg}</b> verified fix${reg>1?'es':''} regressed</a>`);
  if (stalled) chips.push(html`<a class="wchip down" href="#s7"><b>${stalled}</b> stalled waiting on client &gt;14d</a>`);
  if (pend) chips.push(html`<a class="wchip" href="#s7"><b>${pend}</b> implemented — recheck to verify</a>`);
  let D = ws.diff && ws.diff.diff;
  // Stale-diff guard: the diff is snapshotted at ingest. If the loaded run's
  // AI ownership no longer matches the diff's "now" side (the run was
  // re-enriched/rebuilt after indexing), every chip in it describes a payload
  // that no longer exists — showing it would contradict §3. Treat as no diff.
  if (D && D.ai && state.aiNarrative && typeof state.aiNarrative.sourceOwnership === 'number'
      && Math.abs(D.ai.nowShare - state.aiNarrative.sourceOwnership) > 0.005) D = null;
  if (D) {
    const when = ws.diff && ws.diff.prevRunAt ? ` since ${ws.diff.prevRunAt.slice(0,10)}` : '';
    if (D.search && D.search.perTerm) {
      D.search.perTerm.filter((r) => r.prevOwned !== r.nowOwned).slice(0,3).forEach((r) => {
        const up = r.nowOwned > r.prevOwned;
        chips.push(html`<a class="wchip ${up?'up':'down'}" href="#s1">${r.term.split('·')[1]||r.term}: <b>${r.prevOwned}→${r.nowOwned}</b>/${r.slots} owned</a>`);
      });
    }
    if (D.ai && D.ai.prevShare !== D.ai.nowShare) {
      const up = D.ai.nowShare > D.ai.prevShare;
      chips.push(html`<a class="wchip ${up?'up':'down'}" href="#s3">AI citations from client sources: <b>${pct(D.ai.prevShare)}%→${pct(D.ai.nowShare)}%</b></a>`);
    }
    if (D.conflicts && D.conflicts.resolved && D.conflicts.resolved.length)
      chips.push(html`<a class="wchip up" href="#s6"><b>${D.conflicts.resolved.length}</b> conflict${D.conflicts.resolved.length>1?'s':''} resolved${when}</a>`);
    if (D.linkGraph && D.linkGraph.weakChains.prev !== D.linkGraph.weakChains.now) {
      const up = D.linkGraph.weakChains.now < D.linkGraph.weakChains.prev;
      chips.push(html`<a class="wchip ${up?'up':'down'}" href="#s2">unverified profiles: <b>${D.linkGraph.weakChains.prev}→${D.linkGraph.weakChains.now}</b></a>`);
    }
    if (D.assets && (D.assets.added.length || D.assets.removed.length))
      chips.push(html`<a class="wchip info" href="#s8">surfaces: +${D.assets.added.length} / −${D.assets.removed.length}</a>`);
    if (D.notCompared && D.notCompared.length)
      chips.push(html`<span class="wchip info" title="these planes were absent in one of the two runs (scope differs) — deltas would be misleading">not compared: ${D.notCompared.join(', ')}</span>`);
  } else if (!reg && !pend && !stalled) {
    chips.push(html`<span class="wchip info">baseline run — deltas appear after the next comparable research run</span>`);
  }
  render(
    chips.length
      ? html`${scopeTpl}<div class="wsstrip">${chips}</div>`
      : scopeTpl,
    el,
  );
}

// F4 item 3 — see the module doc comment.
wsChangeStore.subscribe(() => renderStrip(WS, payloadStore.get()));
